/**
 * 🧾📷 Grocery Receipt Scanner — Cassandra's Ledger camera modal.
 *
 * Shared across Kitchen 🍳 and Gym 🏋️. Owner canon Feb 12, 2026:
 * "add this in the kitchen/gym and make it interchangeable".
 *
 * Flow:
 *   1. Tap 📷 button → modal opens with camera input.
 *   2. User captures / picks receipt photo (mobile prefers
 *      environment-facing camera via `capture="environment"`).
 *   3. Client compresses to JPEG (max ~1600px long-edge, quality 0.82)
 *      then POST /api/receipts/scan → GPT Vision returns structured
 *      JSON (store, date, subtotal, tax, total, line_items[], …).
 *   4. User reviews the parsed preview; can Save → /api/receipts/save
 *      or Discard.
 *
 * Backing endpoints:
 *   POST /api/receipts/scan      — vision parse only (no persist)
 *   POST /api/receipts/save      — persist + roll up Cassandra Ledger
 *   GET  /api/cassandra-ledger   — running totals snapshot
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_LONG_EDGE = 1600;
const JPEG_QUALITY  = 0.82;

async function compressToJpegBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the image."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not decode the image."));
      img.onload = () => {
        const scale = Math.min(1, MAX_LONG_EDGE / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
        resolve({ dataUrl, mime: "image/jpeg", w, h });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function money(v, currency = "USD") {
  if (v == null || Number.isNaN(v)) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: currency || "USD",
    }).format(Number(v));
  } catch {
    return `$${Number(v).toFixed(2)}`;
  }
}

export default function ReceiptScanner({ open, onClose, capturedFrom = "kitchen", onSaved }) {
  const [phase,     setPhase]     = useState("capture"); // capture | scanning | review | saving
  const [preview,   setPreview]   = useState(null);      // { dataUrl, mime }
  const [parsed,    setParsed]    = useState(null);
  const [err,       setErr]       = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setPhase("capture"); setPreview(null); setParsed(null); setErr("");
    }
  }, [open]);

  if (!open) return null;

  const roomLabel = capturedFrom === "gym" ? "🏋️ Gym" : "🍳 Kitchen";

  async function handleFile(e) {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!/image\/(jpeg|jpg|png|webp)/i.test(file.type)) {
      setErr("Please pick a JPEG, PNG, or WEBP image.");
      return;
    }
    setErr("");
    setPhase("scanning");
    try {
      const compressed = await compressToJpegBase64(file);
      setPreview(compressed);
      const r = await axios.post(`${API}/receipts/scan`, {
        image_base64: compressed.dataUrl,
        mime: compressed.mime,
      });
      setParsed(r.data?.parsed || null);
      setPhase("review");
    } catch (ex) {
      const msg = ex?.response?.data?.detail || ex?.message || "OCR failed.";
      setErr(String(msg));
      setPhase("capture");
      setPreview(null);
    }
  }

  async function handleSave() {
    if (!preview || !parsed) return;
    setPhase("saving");
    try {
      const r = await axios.post(`${API}/receipts/save`, {
        image_base64:  preview.dataUrl,
        mime:          preview.mime,
        parsed,
        captured_from: capturedFrom,
      });
      toast.success(`Receipt saved to Cassandra's Ledger.`);
      if (onSaved) onSaved(r.data);
      onClose?.();
    } catch (ex) {
      const msg = ex?.response?.data?.detail || ex?.message || "Save failed.";
      setErr(String(msg));
      setPhase("review");
    }
  }

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3"
      data-testid="receipt-scanner-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl border border-amber-500/40 bg-[#0f0b06] shadow-[0_0_40px_rgba(245,158,11,0.25)]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-amber-500/20 px-5 py-3">
          <div>
            <div className="text-lg font-black tracking-wide text-amber-300"
                 style={{ fontFamily: "Anton, sans-serif" }}>
              🧾 CASSANDRA&apos;S LEDGER
            </div>
            <div className="text-[11px] text-neutral-400 mt-0.5">
              Snap a grocery receipt · Captured from {roomLabel}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-white text-xl px-2"
            data-testid="receipt-scanner-close"
            aria-label="Close"
          >×</button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {err && (
            <div className="text-sm text-red-300 bg-red-950/40 border border-red-500/40 rounded-lg px-3 py-2"
                 data-testid="receipt-scanner-error">
              {err}
            </div>
          )}

          {phase === "capture" && (
            <div className="space-y-4">
              <p className="text-sm text-neutral-300">
                Line up the receipt in good light. Ledger auto-extracts
                store, date, line items, tax, and total.
              </p>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                onChange={handleFile}
                className="hidden"
                data-testid="receipt-scanner-file-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                className="w-full py-4 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-600 text-black font-black tracking-wide text-lg hover:brightness-110 transition"
                data-testid="receipt-scanner-capture-btn"
              >
                📷 Open Camera / Pick Photo
              </button>
              <div className="text-[11px] text-neutral-500 text-center">
                JPEG / PNG / WEBP · up to 10 MB · we compress before upload
              </div>
            </div>
          )}

          {phase === "scanning" && (
            <div className="text-center py-10" data-testid="receipt-scanner-scanning">
              <div className="text-4xl animate-pulse">🧾</div>
              <div className="mt-3 text-amber-300 font-bold">
                Cassandra is reading your receipt…
              </div>
              <div className="text-xs text-neutral-500 mt-1">
                (usually 5–15 seconds)
              </div>
            </div>
          )}

          {phase === "review" && parsed && preview && (
            <div className="space-y-4" data-testid="receipt-scanner-review">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-lg overflow-hidden border border-neutral-800 bg-black/40">
                  <img
                    src={preview.dataUrl}
                    alt="Receipt preview"
                    className="w-full h-full object-contain max-h-72"
                  />
                </div>
                <div className="space-y-2 text-sm">
                  <RowKv k="Store"    v={parsed.store    || "—"} />
                  <RowKv k="Date"     v={parsed.date     || "—"} />
                  <RowKv k="Currency" v={parsed.currency || "USD"} />
                  <RowKv k="Subtotal" v={money(parsed.subtotal, parsed.currency)} />
                  <RowKv k="Tax"      v={money(parsed.tax,      parsed.currency)} />
                  <RowKv k="Total"    v={money(parsed.total,    parsed.currency)}
                                       accent="amber" />
                </div>
              </div>

              {(parsed.line_items || []).length > 0 && (
                <div className="rounded-lg border border-neutral-800 bg-black/30">
                  <div className="px-3 py-2 border-b border-neutral-800 text-[11px] uppercase tracking-wider text-neutral-500">
                    Line items · {parsed.line_items.length}
                  </div>
                  <div className="max-h-52 overflow-y-auto divide-y divide-neutral-900">
                    {parsed.line_items.map((li, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-xs"
                           data-testid={`receipt-line-item-${i}`}>
                        <span className="flex-1 truncate">{li.name}</span>
                        <span className="text-neutral-500">×{li.qty || 1}</span>
                        <span className="text-[10px] uppercase text-neutral-500 min-w-[70px] text-right">
                          {li.category || "other"}
                        </span>
                        <span className="text-amber-300 font-mono min-w-[60px] text-right">
                          {money(li.price, parsed.currency)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  onClick={() => { setPhase("capture"); setPreview(null); setParsed(null); }}
                  className="flex-1 py-3 rounded-xl bg-neutral-800 hover:bg-neutral-700 text-neutral-200 font-bold"
                  data-testid="receipt-scanner-retake"
                >
                  Retake
                </button>
                <button
                  onClick={handleSave}
                  className="flex-[2] py-3 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-600 text-black font-black tracking-wide hover:brightness-110"
                  data-testid="receipt-scanner-save"
                >
                  💾 Save to Ledger
                </button>
              </div>
            </div>
          )}

          {phase === "saving" && (
            <div className="text-center py-10" data-testid="receipt-scanner-saving">
              <div className="text-4xl animate-pulse">💾</div>
              <div className="mt-3 text-amber-300 font-bold">Filing to Cassandra&apos;s Ledger…</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RowKv({ k, v, accent }) {
  return (
    <div className="flex items-center gap-2">
      <div className="text-[11px] uppercase tracking-wider text-neutral-500 min-w-[80px]">{k}</div>
      <div className={`flex-1 truncate ${accent === "amber" ? "text-amber-300 font-black text-lg" : "text-neutral-200"}`}>
        {v}
      </div>
    </div>
  );
}
